import { Icon, Text } from "@chakra-ui/react";
import { MdPersonRemove } from "react-icons/md";
import DeleteConfirmModalButton from "components/Shared/ConfirmModal/DeleteConfirmModalButton";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { useCurrentBrandData } from "utils/currentUserHooks";
import { assertPresence } from "utils/assertions";
import { APP_NAME } from "utils/config";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import UserChip from "components/Shared/UserChip";
import Well from "components/Shared/Well";

const fragment = gql(/* GraphQL */ `
  fragment RemoveBrandUserButton on BrandUser {
    id
    user {
      id
      ...UserChipComponent
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation RemoveBrandUser($id: UUID!) {
    deleteBrandUser(input: { id: $id }) {
      brandUser {
        id
      }
    }
  }
`);

type Props = {
  brandUser: FragmentType<typeof fragment>;
};

export default function RemoveBrandUserButton({
  brandUser: brandUserFragment,
}: Props) {
  const brand = useCurrentBrandData();
  const brandUser = getFragmentData(fragment, brandUserFragment);

  const mutationTuple = useBrandMutation(mutation, {
    variables: { id: brandUser.id },
    update: (cache, { data }) => {
      const deletedBrandUser = data?.deleteBrandUser?.brandUser;
      assertPresence(deletedBrandUser);
      removeDeletedRecords({
        cache,
        baseFieldName: "brandUsers",
        deletedRecords: [deletedBrandUser],
      });
    },
  });

  return (
    <DeleteConfirmModalButton
      mutation={mutationTuple}
      label="Remove User"
      confirmButtonLabel="Remove"
      confirmText={
        <>
          <Text>
            Removed users will lose access to the {APP_NAME} account for{" "}
            {brand?.name}.
          </Text>
          <Text my={4}>
            Are you sure you want to remove this user from <b>{brand?.name}</b>?
          </Text>
          <Well mt={6} mb={2}>
            <UserChip user={brandUser.user} />
          </Well>
        </>
      }
      buttonProps={{
        icon: <Icon as={MdPersonRemove} />,
        size: "md",
      }}
    />
  );
}
